import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import CollapsibleCard from '@core/components/CollapsibleCard';
import { getScopeFromValue } from '@features/invitations/utils/getScopeFromParam';
import { fetchMembershipHistory } from '../api/membershipApi';
import type { MembershipHistoryMonth, MembershipSummary, MembershipTallies } from '../types';
import { comparisonLabel, formatCount, formatShortDateTime } from '../utils/membershipFormat';
import LastUpdatedChurchesModal from './LastUpdatedChurchesModal';
import MembershipHistoryTable from './MembershipHistoryTable';
import SetMembershipCountModal from './SetMembershipCountModal';
import UpdateMembershipModal from './UpdateMembershipModal';
import '../membership.css';

interface Props {
  membership: MembershipSummary;
  apiPath: string;
  scope: string;
  canUpdate: boolean;
  canOverride: boolean;
  onSaved: () => void;
}

const TALLY_TILES: Array<{ key: keyof MembershipTallies; label: string; sign: '+' | '-' }> = [
  { key: 'added', label: 'Added', sign: '+' },
  { key: 'transferred_in', label: 'Transferred in', sign: '+' },
  { key: 'deceased', label: 'Deceased', sign: '-' },
  { key: 'transferred_out', label: 'Transferred out', sign: '-' },
  { key: 'excluded', label: 'Excluded', sign: '-' },
];

function membershipSubtitle(scope: 'CHURCH' | 'DISTRICT' | 'NATION'): string {
  if (scope === 'DISTRICT') return "The district's membership as it stands today";
  if (scope === 'NATION') return "The nation's membership as it stands today";
  return "Your church's membership as it stands today";
}

function recordedLabel(value?: string | null): string {
  return value ? formatShortDateTime(value) : 'Not updated yet';
}

function shareOf(part?: number | null, total?: number | null): number {
  if (!total || !part) return 0;
  return Math.round((part / total) * 100);
}

const MembershipSection: React.FC<Props> = ({
  membership,
  apiPath,
  scope,
  canUpdate,
  canOverride,
  onSaved,
}) => {
  const [cardOpen, setCardOpen] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [months, setMonths] = useState<MembershipHistoryMonth[]>([]);
  const [visible, setVisible] = useState(3);
  const [hasMore, setHasMore] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [showChurches, setShowChurches] = useState(false);
  const churchUpdates = membership.last_updated_churches ?? [];
  const updatedChurches = churchUpdates.filter((church) => church.last_updated_at);
  const latestUpdate = updatedChurches.map((church) => church.last_updated_at as string).sort().pop() ?? null;
  const scopeKind = (getScopeFromValue(scope) || 'CHURCH') as 'CHURCH' | 'DISTRICT' | 'NATION';
  const isChurch = scopeKind === 'CHURCH';
  const delta = comparisonLabel(membership.comparison, membership.total_members, membership.last_month_total);
  const tallies = membership.tallies;
  const loadedPath = useRef<string | null>(null);
  const malePct = shareOf(membership.male_count, membership.total_members);
  const femalePct = shareOf(membership.female_count, membership.total_members);

  useEffect(() => {
    if (loadedPath.current === null || loadedPath.current === apiPath) {
      loadedPath.current = apiPath;
      return;
    }
    loadedPath.current = apiPath;
    setMonths([]);
    setHasMore(false);
    setHistoryLoaded(false);
    setVisible(3);
  }, [apiPath]);

  useEffect(() => {
    if (!membership.show_details || !historyOpen || !cardOpen) return;
    let cancelled = false;
    fetchMembershipHistory(apiPath, visible).then((response) => {
      if (cancelled) return;
      setHistoryLoaded(true);
      if (!response?.isSuccess() || !response.getData()) return;
      setMonths(response.getData()!.months);
      setHasMore(response.getData()!.has_more);
    });
    return () => {
      cancelled = true;
    };
  }, [membership.show_details, historyOpen, cardOpen, apiPath, visible]);

  const actions = isChurch && (canUpdate || canOverride) && (
    <div className="membership-card__actions">
      {canOverride && (
        <Button variant="link" className="membership-muted-link px-0" onClick={() => setShowOverride(true)}>
          Set membership count
        </Button>
      )}
      {canUpdate && (
        <Button variant="primary" size="sm" onClick={() => setShowUpdate(true)}>Update membership</Button>
      )}
    </div>
  );

  return (
    <CollapsibleCard
      className="ntcog-card dashboard-section-card membership-card membership-card--modern mb-4"
      title="Membership"
      subtitle={membershipSubtitle(scopeKind)}
      subtitleClassName="membership-subtitle text-muted small mb-0"
      actions={actions}
      collapsible={membership.show_details}
      open={cardOpen}
      onToggle={setCardOpen}
      bodyId="membership-card-body"
      footer={
        <>
          {!isChurch && membership.show_details && (
            <LastUpdatedChurchesModal show={showChurches} churches={churchUpdates} onHide={() => setShowChurches(false)} />
          )}
          {isChurch && canUpdate && (
            <UpdateMembershipModal
              show={showUpdate}
              apiPath={apiPath}
              onHide={() => setShowUpdate(false)}
              onSaved={onSaved}
            />
          )}
          {isChurch && canOverride && (
            <SetMembershipCountModal
              show={showOverride}
              apiPath={apiPath}
              onHide={() => setShowOverride(false)}
              onSaved={onSaved}
            />
          )}
        </>
      }
    >
      <div className={`membership-stats${membership.show_details ? '' : ' membership-stats--3'}`}>
        <div className="membership-stat">
          <div className="membership-stat__label">Total members</div>
          <div className="membership-stat__value">{formatCount(membership.total_members)}</div>
        </div>
        {membership.show_details && (
          <div className="membership-stat">
            <div className="membership-stat__label">Membership last month</div>
            <div className="membership-stat__value">
              {membership.last_month_total == null ? '\u2014' : formatCount(membership.last_month_total)}
            </div>
            {delta && membership.comparison === 'up' && (
              <div className="membership-tile__trend membership-tile__trend--up">
                <span aria-hidden="true">{'\u25B2 '}</span>
                <span>{delta}</span>
              </div>
            )}
            {delta && membership.comparison === 'down' && (
              <div className="membership-tile__trend membership-tile__trend--down">
                <span aria-hidden="true">{'\u25BC '}</span>
                <span>{delta}</span>
              </div>
            )}
            {membership.comparison === 'flat' && (
              <div className="membership-tile__trend membership-tile__trend--flat">Same as last month</div>
            )}
          </div>
        )}
        <div className="membership-stat">
          <div className="membership-stat__label">Male</div>
          <div className="membership-stat__value">{formatCount(membership.male_count)}</div>
          <div className="membership-stat__bar" aria-hidden="true"><span style={{ width: `${malePct}%` }} /></div>
          <div className="membership-stat__note">{malePct}% of members</div>
        </div>
        <div className="membership-stat">
          <div className="membership-stat__label">Female</div>
          <div className="membership-stat__value">{formatCount(membership.female_count)}</div>
          <div className="membership-stat__bar" aria-hidden="true"><span style={{ width: `${femalePct}%` }} /></div>
          <div className="membership-stat__note">{femalePct}% of members</div>
        </div>
      </div>

      {membership.show_details && (
        <>
          <h3 className="membership-section-label">Current in-app records</h3>
          <p className="membership-hint mb-2">Running totals of the changes recorded in this application</p>
          <div className="membership-records">
            {TALLY_TILES.map((tile) => (
              <div className="membership-record" key={tile.key}>
                <div className="membership-record__label">
                  <span
                    className={`membership-record__sign membership-record__sign--${tile.sign === '+' ? 'in' : 'out'}`}
                    aria-hidden="true"
                  >
                    {tile.sign === '+' ? '+' : '\u2212'}
                  </span>
                  {tile.label}
                </div>
                <div className="membership-record__value">{formatCount(tallies?.[tile.key] ?? 0)}</div>
              </div>
            ))}
          </div>

          <div className="membership-history-block">
            <h3 className="h6 mb-2">
              <button
                type="button"
                className="membership-caret btn btn-link btn-sm"
                aria-expanded={historyOpen}
                aria-controls="membership-history"
                onClick={() => setHistoryOpen((value) => !value)}
              >
                <span className="membership-caret__mark" aria-hidden="true">{historyOpen ? '\u25BE' : '\u25B8'}</span>
                Membership History
              </button>
            </h3>
            {historyOpen && (
              <div id="membership-history">
                <div className="membership-updated-summary">
                  {isChurch ? (
                    <>Last updated: {recordedLabel(membership.last_updated_at)}</>
                  ) : (
                    <>
                      Last updated: {recordedLabel(latestUpdate)} {'\u00B7'} {updatedChurches.length} of {churchUpdates.length} churches updated {'\u00B7'}{' '}
                      <Button variant="link" className="membership-view-churches" onClick={() => setShowChurches(true)}>
                        View churches
                      </Button>
                    </>
                  )}
                </div>
                {historyLoaded && (
                  <MembershipHistoryTable
                    scope={scopeKind}
                    months={months}
                    hasMore={hasMore}
                    onShowMore={() => setVisible((value) => value + 3)}
                    onShowLess={() => setVisible(3)}
                  />
                )}
              </div>
            )}
          </div>
        </>
      )}
    </CollapsibleCard>
  );
};

export default MembershipSection;
